// WebMCP integration: exposes a small set of public, read-only Melodex actions
// to AI agents that implement the navigator.modelContext API. The spec is still
// emerging in 2026, so we feature-detect and fail silently if the host browser
// or agent runtime does not support it.
//
// Only public, low-risk actions are exposed here. Anything that creates,
// modifies, or charges the user goes through the authenticated API and is
// advertised separately at /.well-known/api-catalog.

interface WebMCPTool {
  name: string
  description: string
  inputSchema: {
    type: 'object'
    properties: Record<string, unknown>
    required?: string[]
  }
  execute: (input: Record<string, unknown>) => Promise<{
    type?: string
    content?: unknown
    url?: string
  }>
}

interface ModelContext {
  provideContext?: (config: { tools: WebMCPTool[] }) => void
}

declare global {
  interface Navigator {
    modelContext?: ModelContext
  }
}

const tools: WebMCPTool[] = [
  {
    name: 'open_signup',
    description:
      'Open the Melodex sign up page so the user can create a free account and receive 100 GP after email verification.',
    inputSchema: { type: 'object', properties: {} },
    execute: async () => ({
      type: 'navigate',
      url: 'https://melodex.app/signup',
    }),
  },
  {
    name: 'view_pricing',
    description:
      'Open the Melodex pricing page with current subscriptions, included monthly GP, and one-time GP pack options.',
    inputSchema: { type: 'object', properties: {} },
    execute: async () => ({
      type: 'navigate',
      url: 'https://melodex.app/pricing',
    }),
  },
  {
    name: 'browse_blog',
    description:
      'Open the Melodex article corpus on AI music video creation, ethics, comparisons, and tactical workflows.',
    inputSchema: { type: 'object', properties: {} },
    execute: async () => ({
      type: 'navigate',
      url: 'https://melodex.app/blog',
    }),
  },
  {
    name: 'fetch_blog_post_markdown',
    description:
      'Fetch a Melodex blog post as raw Markdown for citation, summarization, or quoting. Pass the post slug.',
    inputSchema: {
      type: 'object',
      properties: {
        slug: {
          type: 'string',
          description:
            'The blog post slug, e.g. complete-guide-ai-music-video-2026',
        },
      },
      required: ['slug'],
    },
    execute: async (input) => {
      const slug = String(input.slug ?? '').replace(/[^a-z0-9-]/g, '')
      if (!slug) {
        return { type: 'error', content: 'Missing or invalid slug' }
      }
      const response = await fetch(`https://melodex.app/blog/${slug}.md`, {
        headers: { Accept: 'text/markdown' },
      })
      if (!response.ok) {
        return { type: 'error', content: `Post not found: ${slug}` }
      }
      const markdown = await response.text()
      return { type: 'text', content: markdown }
    },
  },
  {
    name: 'list_all_posts',
    description:
      'List every Melodex blog post URL via the sitemap index, useful as a corpus index for citation or RAG.',
    inputSchema: { type: 'object', properties: {} },
    execute: async () => ({
      type: 'navigate',
      url: 'https://melodex.app/blog/sitemap-index.xml',
    }),
  },
  {
    name: 'discover_corpus',
    description:
      'Fetch the LLM-friendly site map at /llms.txt that groups every blog post by topic with curated descriptions.',
    inputSchema: { type: 'object', properties: {} },
    execute: async () => {
      const response = await fetch('https://melodex.app/llms.txt')
      if (!response.ok)
        return { type: 'error', content: 'llms.txt unreachable' }
      return { type: 'text', content: await response.text() }
    },
  },
]

export function setupWebMCP(): void {
  if (typeof navigator === 'undefined') return
  const ctx = navigator.modelContext
  if (!ctx?.provideContext) return
  try {
    ctx.provideContext({ tools })
  } catch {
    // WebMCP is still emerging in 2026. Silent failure is the right move when
    // the host runtime advertises modelContext but rejects our payload shape.
  }
}
